import Cart from '@components/Cart';
import { Fragment, useEffect, useState } from 'react';
import Spacer from '@components/Spacer';

import { useIsMobile } from '@hooks';
import ReactGA from 'react-ga';
import { getEcommerceItemFromProduct } from '@helpers/ecommerce';
import useCart from '@hooks/useCart';
import { gaEvent } from '@helpers/analytics';
import { useRouter } from 'next/router';
import { NoodleProductTypes } from '@typings/graphql-models';
import ProgressIndicator from '@components/ProgressIndicator';
import CheckoutNew from '@components/CheckoutNew';
import { getProductBySlug } from '@tsClient';
import deserializeQueryStringItem from '@helpers/deserializeQueryStringItem';
import ShoppingCart from '@providers/ShoppingCart';
import useNoodleApi from '@hooks/useNoodleApi';
import { logError } from '@providers/ErrorTracking';

import classes from './cartCheckoutNew.module.scss';

type Props = {
  creatorSlug: string;
};

const CartCheckoutNew: React.FC<Props> = ({ creatorSlug }) => {
  const router = useRouter();
  const isMobile = useIsMobile();
  const [onlyFreeProducts, setOnlyFreeProducts] = useState(false);
  const [freeMemberships, setFreeMemberships] = useState(false);
  const [hasChat, setHasChat] = useState(false);
  const [isFreeBooking, setIsFreeBooking] = useState(false);
  const [isAddingPrice, setIsAddingPrice] = useState(false);

  const [freeMembershipTitle, setFreeMembershipTitle] = useState<string>('');

  const priceId = deserializeQueryStringItem(router.query.priceId, { type: 'string' });
  const productSlug = deserializeQueryStringItem(router.query.productSlug, { type: 'string' });

  const { getData: getProduct } = useNoodleApi(getProductBySlug);

  const isFreeMembership = router?.query?.isFreeMembership === 'true';
  const isSaaSProduct = router?.query?.isSaasProduct === 'true';

  const { cart } = useCart({ creatorSlug });

  useEffect(() => {
    if (isFreeMembership) {
      const freeMembership = cart?.filter(p => p.product.productTypes?.some(t => t.noodleProductType === NoodleProductTypes.Subscription));
      setFreeMembershipTitle(freeMembership?.[0]?.product?.title || '');
      setFreeMemberships(freeMembership?.length > 0);
    } else {
      const isFree = cart.every(p => p.price === 0);
      if (cart.length === 1) {
        setHasChat(cart[0].product.productTypes.every((t) => t?.noodleProductType ===NoodleProductTypes.Lite));
        setIsFreeBooking(cart[0].product.productTypes.every((t) => t?.noodleProductType === NoodleProductTypes.OnlineSession
        || t?.noodleProductType === NoodleProductTypes.InPersonSession) && cart[0].price === 0);
      } else {
        setHasChat(false);
      }
      setOnlyFreeProducts(isFree);
    }
  }, [cart, isFreeMembership]);

  useEffect(() => {
    cart.forEach(price => {
      ReactGA.plugin.execute('ec', 'addProduct', getEcommerceItemFromProduct(price.product, price));
    });
    ReactGA.plugin.execute('ec', 'setAction', 'checkout', {
      step: 1,
    });
    gaEvent('begin_checkout', {
      items: cart.map(price => getEcommerceItemFromProduct(price.product, price)),
    });
  }, [cart]);

  useEffect(() => {
    if (priceId && productSlug && !cart.find((c) => c.id === priceId)) {
      const addPriceToCart = async (): Promise<void> => {
        setIsAddingPrice(true);
        const { data } = await getProduct({
          creatorSlug,
          productSlug,
        });
        const price = data?.prices.find((p) => p.id === priceId);
        if (price) {
          await ShoppingCart.addProduct({ price });
        } else {
          logError(new Error('Trying to add nonexistent price to cart'), {
            tags: { flow: 'purchase' },
          });
        }
        setIsAddingPrice(false);
      };
      addPriceToCart();
    }
  }, [creatorSlug, getProduct, productSlug, priceId, cart]);

  if (!creatorSlug || isAddingPrice) {
    return <ProgressIndicator isCentered />;
  }

  return (
    <div className={classes.container}>
      <Fragment>
        <CheckoutNew
          creatorSlug={creatorSlug}
          isSaaSProduct={isSaaSProduct}
        />
      </Fragment>
      {(!hasChat || !onlyFreeProducts || !freeMemberships)
      && <div className={classes.cartContainer}>
        {!isMobile && (
          <>
            <span className={classes.secondary}>Order Summary</span>
            <Spacer size="24px" />
          </>
        )}
        <Cart
          creatorSlug={creatorSlug}
          showForeignCurrency={true}
          onlyFreeProducts={onlyFreeProducts}
          isFreeMembership={isFreeMembership}
          isFreeBooking={isFreeBooking}
          isSaaSProduct={isSaaSProduct}
          freeMembershipTitle={freeMembershipTitle}
        />
      </div>
      }
    </div>
  );
};

export default CartCheckoutNew;
